import { CheckCircle } from "lucide-react";

interface CurriculumSessionProps {
  session: number;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  isLeft?: boolean;
  outcomes?: string[];
}

export const CurriculumSession = ({
  session,
  title,
  subtitle,
  description,
  icon,
  isLeft = true,
  outcomes = [],
}: CurriculumSessionProps) => {
  const borderColors = [
    "border-l-primary",
    "border-l-primary/80",
    "border-l-secondary",
    "border-l-accent/80",
    "border-l-accent",
  ];
  const borderColor = borderColors[session - 1] || "border-l-primary";

  return (
    <div className="relative flex items-center gap-6 md:gap-10">
      <div className="hidden md:flex flex-col items-center absolute left-1/2 -translate-x-1/2 z-20">
        <div className="w-14 h-14 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center shadow-[0_0_20px_hsl(var(--primary)/0.3)]">
          <span className="text-lg font-bold text-primary">{session}</span>
        </div>
      </div>

      <div
        className={`w-full md:w-[calc(50%-3rem)] ${
          isLeft ? "md:mr-auto" : "md:ml-auto"
        }`}
      >
        <div
          className={`glass-card p-6 md:p-8 border-l-4 ${borderColor} hover-lift group`}
        >
          <div className="md:hidden flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-primary">{session}</span>
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-primary/30 to-transparent" />
          </div>

          <div className="flex items-start gap-4">
            <div className="text-primary shrink-0 group-hover:scale-110 transition-transform duration-300">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="text-xl md:text-2xl font-bold mb-1">{title}</h3>
              <h4 className="text-base md:text-lg text-foreground/80">
                {subtitle}
              </h4>
              <p className="text-muted-foreground text-sm md:text-base">
                {description}
              </p>

              {outcomes.length > 0 && (
                <div className="pt-2 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary/70">
                    You'll be able to:
                  </p>
                  <ul className="space-y-1.5">
                    {outcomes.map((outcome) => (
                      <li
                        key={outcome}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <CheckCircle className="w-4 h-4 text-primary/60 shrink-0 mt-0.5" />
                        <span>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
